import React, { useState, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import logoOrig from '../img/tmis_logo.png';
import logoWhite from '../img/tmis_logo_w.png';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usePopupCard, PopupCard, PopupCardButton } from './FlyoutCard';

const NAV_ITEMS = [
  { label: 'Claims for Payment', to: '/finance', end: true, icon: 'bi-receipt' },
];

function initials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
}

// Mirrors TrainerLayout's rail/nav structure (and its trn-* CSS classes) so
// the Finance role looks native to the app rather than a bolted-on screen.
export default function FinanceLayout({ children }) {
  const { user, avatarColor, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate          = useNavigate();
  const location          = useLocation();
  const isDark            = theme === 'dark';
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sms_finance_rail_collapsed') === '1');
  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sms_finance_rail_collapsed', next ? '1' : '0');
      return next;
    });
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const railRef = useRef(null);
  const getSidebarRect = useCallback(() => railRef.current ? railRef.current.getBoundingClientRect() : null, []);
  const acctMenu = usePopupCard({ getSidebarRect, mode: 'account' });

  return (
    <div className="trn-app">
      <aside ref={railRef} className={`trn-rail ${collapsed ? 'collapsed' : ''}`} aria-label="Finance portal navigation">
        <div
          className="trn-rail-head sms-sidebar-head"
          onClick={collapsed ? toggleCollapsed : undefined}
          role={collapsed ? 'button' : undefined}
          tabIndex={collapsed ? 0 : undefined}
          onKeyDown={collapsed ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCollapsed(); } }) : undefined}
          aria-label={collapsed ? 'Expand sidebar' : undefined}
        >
          <img src="/favicon.svg" alt="TMIS SMS" className="trn-brand-collapsed-icon" />
          <div className="trn-brand">
            <img src={isDark ? logoWhite : logoOrig} alt="TMIS SMS" className="trn-brand-logo" />
          </div>
          <button
            className="trn-collapse-btn sms-sidebar-collapse-btn"
            onClick={e => { e.stopPropagation(); toggleCollapsed(); }}
            aria-pressed={collapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <i className="bi bi-chevron-bar-left" aria-hidden="true" />
          </button>
        </div>

        <nav className="trn-nav sms-panel-body" aria-label="Primary">
          {!collapsed && (
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', color: 'var(--sb-panel-sec-lbl)', padding: '0 12px 6px' }}>
              Finance
            </div>
          )}
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-label={item.label}
              className={({ isActive }) => `trn-nav-item sms-panel-item ${isActive ? 'active' : ''}`}
            >
              <span className="sms-nav-icon-col"><i className={`bi ${item.icon}`} aria-hidden="true" /></span>
              <span className="sms-panel-item-label">{item.label}</span>
            </NavLink>
          ))}
          {/* Placeholder for a future module — keeps the rail from reading as
              a single stray item floating above a large empty gap while the
              Finance role only has one real page. */}
          <div className="trn-nav-item sms-panel-item" data-label="Payment History"
            style={{ cursor: 'default', opacity: .5, justifyContent: collapsed ? 'center' : 'space-between' }}
            aria-disabled="true">
            <span style={{ display: 'flex', alignItems: 'center', gap: 9, overflow: 'hidden' }}>
              <span className="sms-nav-icon-col"><i className="bi bi-clock-history" aria-hidden="true" /></span>
              <span>Payment History</span>
            </span>
            {!collapsed && (
              <span style={{
                fontSize: 12, fontWeight: 700, letterSpacing: '.3px', textTransform: 'uppercase',
                color: 'var(--sms-txt3)', background: 'var(--sms-sur2)', border: '1px solid var(--sb-panel-bdr)',
                borderRadius: 999, padding: '2px 7px', flexShrink: 0,
              }}>
                Soon
              </span>
            )}
          </div>
        </nav>

        <div className="trn-rail-foot sms-panel-foot-wrap">
          <div className="sms-sidebar-theme-toggle" role="group" aria-label="Theme">
            <button type="button" className={`sms-sidebar-theme-seg ${!isDark ? 'active' : ''}`}
              onClick={() => isDark && toggle()} aria-pressed={!isDark}>
              <i className="bi bi-sun-fill" aria-hidden="true" />
              <span className="sms-sidebar-theme-seg-label">Light</span>
            </button>
            <button type="button" className={`sms-sidebar-theme-seg ${isDark ? 'active' : ''}`}
              onClick={() => !isDark && toggle()} aria-pressed={isDark}>
              <i className="bi bi-moon-stars-fill" aria-hidden="true" />
              <span className="sms-sidebar-theme-seg-label">Dark</span>
            </button>
          </div>
          <button type="button" className="sms-sidebar-theme-collapsed-btn" onClick={toggle}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            title={isDark ? 'Light mode' : 'Dark mode'}>
            <i className={`bi ${isDark ? 'bi-sun-fill' : 'bi-moon-stars-fill'}`} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="sms-panel-foot-trigger"
            onClick={e => acctMenu.toggle('account', e.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={acctMenu.openId === 'account'}
            aria-label={`Account menu for ${user?.full_name || 'account'}`}
          >
            <div className="sms-panel-foot-avatar" style={{ background: avatarColor || '#7c3aed' }}>
              {initials(user?.full_name) || 'FI'}
            </div>
            <div className="sms-panel-foot-user">
              <div className="sms-panel-foot-name">{user?.full_name}</div>
              <div className="sms-panel-foot-email">{user?.username}</div>
            </div>
            <i className="bi bi-three-dots-vertical sms-panel-foot-more" aria-hidden="true" />
          </button>
        </div>

        <div className="trn-rail-mobile-actions">
          <button className="trn-icon-btn" onClick={toggle} aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
            <i className={`bi ${isDark ? 'bi-sun' : 'bi-moon'}`} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="trn-mobile-avatar-btn"
            onClick={e => acctMenu.toggle('account', e.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={acctMenu.openId === 'account'}
            aria-label={`Account menu for ${user?.full_name || 'account'}`}
          >
            <div className="sms-panel-foot-avatar" style={{ background: avatarColor || '#7c3aed' }}>
              {initials(user?.full_name) || 'FI'}
            </div>
          </button>
        </div>
      </aside>

      <div className="trn-main">
        <main id="main-content" className="trn-main-content" tabIndex="-1" key={location.pathname}>
          {children}
        </main>
      </div>

      <nav className="trn-bottom-nav" aria-label="Primary mobile navigation">
        {NAV_ITEMS.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `trn-bottom-nav-item ${isActive ? 'active' : ''}`}
          >
            <i className={`bi ${item.icon}`} aria-hidden="true" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {acctMenu.openId === 'account' && (
        <PopupCard
          pos={acctMenu.pos}
          cardRef={acctMenu.cardRef}
          label={user?.full_name}
          onMouseEnter={acctMenu.cancelClose}
          onMouseLeave={acctMenu.scheduleClose}
        >
          <PopupCardButton
            icon="bi-box-arrow-right"
            label="Log out"
            danger
            onClick={() => { acctMenu.closeNow(); handleLogout(); }}
          />
        </PopupCard>
      )}
    </div>
  );
}
